import { Injectable } from "@angular/core";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Observable } from "rxjs";
import { MessagesUtils } from "../layout/messages.utils";
import { DateUtils } from "../date.utils";
import { ColumnModel } from "@/components/datatable/model/column.model";
import { getNestedValueAttribute, hasItemsGreaterThanZero, isNotNull } from "../functions";

@Injectable({
    providedIn: 'root'
})
export class ExporterUtils {

    constructor(private dateUtils: DateUtils,
        private messagesUtils: MessagesUtils,) {
    }

    exportExcelOrCSV(items: Array<any>, columns: Array<ColumnModel>, type: TypeExportEnum, fileName?: string) {
        try {
            /* generate workbook and add the worksheet */
            const wb: XLSX.WorkBook = XLSX.utils.book_new();
            const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(this.convertListItemsFromNestedObjectsXLSX(items, columns));
            XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
            /* save to file */
            if (!fileName) {
                fileName = `${this.dateUtils.formatDateTimeUUID()}.${type}`;
            }
            XLSX.writeFile(wb, fileName);
        } catch (error) {
            console.log('ERROR | Error on export file excel or CSV.', error);
            this.messagesUtils.showMessageErrorToast('messages.error.exporterFile', { fileType: type });
        }
    }

    convertExcelOrCSVToJSON(fileUploaded: any): Observable<any> {
        return new Observable(subscriber => {
            try {
                let fileReader = new FileReader();
                fileReader.readAsArrayBuffer(fileUploaded);
                fileReader.onload = (e) => {
                    const arrayBuffer: any = fileReader.result;
                    var data = new Uint8Array(arrayBuffer);
                    var arr = new Array();
                    for (var i = 0; i != data.length; ++i) arr[i] = String.fromCharCode(data[i]);
                    var bstr = arr.join("");
                    var workbook = XLSX.read(bstr, { type: "binary" });
                    var first_sheet_name = workbook.SheetNames[0];
                    var worksheet = workbook.Sheets[first_sheet_name];
                    var arraylist = XLSX.utils.sheet_to_json(worksheet, { raw: true });
                    subscriber.next(arraylist);
                    subscriber.complete();
                }
            } catch (error) {
                subscriber.error(error);
            }
        });
    }

    convertListItemsFromNestedObjectsXLSX(items: Array<any>, columns: Array<ColumnModel>): Array<any> {
        const newList: Array<any> = [];
        if (hasItemsGreaterThanZero(items) && hasItemsGreaterThanZero(columns)) {
            // Verifica se tem pelo menos uma coluna com objetos aninhados
            if (columns.find(c => c.nameAttr.includes('.')) != undefined) {
                items.forEach(item => {
                    const newItem: any = {};
                    columns.forEach(col => {
                        if (col.nameAttr) {
                            const value = getNestedValueAttribute(col.nameAttr, item);
                            newItem[col.label] = isNotNull(value) ? String(value) : '';
                        }
                    });
                    newList.push(newItem);
                });
            } else {
                return items;
            }
        }
        return newList;
    }

    exportPDF(title: string, columns: Array<ColumnModel>, items: Array<any>, fileName?: string) {
        try {
            const doc = new jsPDF({
                orientation: 'landscape'
            });
            const columnsConverted = this.createColumnsExporterPDF(columns);

            let pageWidth = 0;
            let pageHeight = 0;
            let totalPages = 0;
            let marginLeft = 0;
            let marginRight = 0;
            autoTable(doc, {
                // tableWidth: 'wrap',
                startY: 30,
                margin: { top: 15, right: 10, bottom: 15, left: 10 },
                columns: columnsConverted,
                body: items,
                theme: "striped",
                showHead: 'everyPage',
                showFoot: 'everyPage',
                columnStyles: {
                    chave: { cellWidth: 70, overflow: 'linebreak' },
                },
                didParseCell: (data) => {
                    // Faz isso para poder pegar o valor de tributos de objetos aninhados
                    const { column, row, cell, section }: any = data;
                    if (section === 'body' && column?.dataKey?.includes('.')) {
                        const valorAninhado = getNestedValueAttribute(column.dataKey, row.raw);
                        cell.text = [isNotNull(valorAninhado) ? String(valorAninhado) : ''];
                    }
                },
                didDrawPage: (event) => {
                    pageWidth = doc.internal.pageSize.width;
                    pageHeight = doc.internal.pageSize.height;
                    const pageNumber = event.table.pageNumber;
                    // Set Header 
                    if (pageNumber === 1) {
                        doc.setFontSize(20);
                        doc.text(title, event.settings.margin.left, 20);

                        const dateFormatted = this.dateUtils.formatDateTimeBrazilian(new Date());
                        const dateReport = `Data: ${dateFormatted}`;
                        doc.setFontSize(12);
                        doc.text(dateReport, (pageWidth - doc.getTextWidth(dateReport)) - event.settings.margin.right, 20);
                    }
                    marginLeft = event.settings.margin.left;
                    marginRight = event.settings.margin.right;
                    totalPages++;
                }
            });

            // Footer
            // For each page per page, print the page number and the total pages
            for (var i = 1; i <= totalPages; i++) {
                // Go to page i
                doc.setPage(i);
                doc.setFontSize(10);
                const regsPerPage = this.getRowsPerPage(items, i);
                const qtdeRegs = `Exibindo ${regsPerPage} registros do total de ${items.length} encontrados`;
                doc.text(qtdeRegs, marginLeft, pageHeight - 10);

                const pageReport = `Página: ${i} de ${totalPages}`;
                doc.text(pageReport, (pageWidth - doc.getTextWidth(pageReport)) - marginRight, pageHeight - 10);
            }

            /* save to file */
            if (!fileName) {
                fileName = `${this.dateUtils.formatDateTimeUUID()}.pdf`;
            }
            doc.save(fileName);
        } catch (error) {
            console.log('ERROR | Error on export file PDF.', error);
            this.messagesUtils.showMessageErrorToast('messages.error.exporterFile', { fileType: 'PDF' });
        }
    }

    /**
     * Page 1 = 32 regs
     * Page 2+ = 34 regs
     */
    getRowsPerPage(items: Array<any>, pageNumber: number) {
        if (pageNumber === 1) {
            const rowsPerPage = items.slice(0, 32);
            return rowsPerPage.length;
        } else {
            const sliceInit = ((pageNumber - 1) * 34) - 2; // Subtrai 2 porque na primeira pagina cabe 2 registros a menos por causa do cabecalho
            const sliceEnd = (pageNumber * 34) - 2;
            const rowsPerPage = items.slice(sliceInit, sliceEnd);
            return rowsPerPage.length;
        }
    }

    createColumnsExporterPDF(columns: Array<ColumnModel>): Array<any> {
        return columns
            .filter(c => c.exportable === 'true')
            .map(c => {
                return {
                    header: c.label,
                    dataKey: c.nameAttr,
                    columnWidth: 'wrap',
                };
            });
    }

}

export enum TypeExportEnum {
    CSV = "csv",
    EXCEL = "xlsx",
    PDF = "pdf",
}